import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, Clock, Shield, Target, GraduationCap, Briefcase } from 'lucide-react';
import { useEffect } from 'react';

export const InstructionsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Auto-determine assessment type based on user registration
  // International students should get the student assessment (career discovery)
  const isStudentType = ['student', 'international_student'].includes(user?.user_type);
  const assessmentType = isStudentType ? 'student' : 'professional';
  const isStudent = assessmentType === 'student';
  
  // Debug log
  console.log('InstructionsPage - user_type:', user?.user_type, 'assessmentType:', assessmentType);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    
    // Check if user has paid - redirect to payment if not
    if (user && !user.is_paid && !user.is_admin) {
      navigate('/payment');
    }
  }, [user, authLoading, navigate]);

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="h-12 w-12 border-4 border-[#CC5500]/20 border-t-[#CC5500] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || (!user.is_paid && !user.is_admin)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="instructions-page">
      {/* Background effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#CC5500]/10 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#C5A065]/10 rounded-full filter blur-[100px]" />
      </div>

      {/* Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0D0D0D]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-heading font-semibold text-white">
              Lead<span className="text-[#CC5500]">Economics</span>™
            </h1>
            <div className="text-sm text-gray-400">
              Welcome back, {user.full_name}
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          {/* Assessment Type Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#CC5500]/20 to-[#C5A065]/20 border border-[#CC5500]/30 mb-6">
            {isStudent ? (
              <>
                <GraduationCap className="h-5 w-5 text-[#C5A065]" />
                <span className="text-sm font-medium text-[#C5A065]">Student Assessment</span>
              </>
            ) : (
              <>
                <Briefcase className="h-5 w-5 text-[#CC5500]" />
                <span className="text-sm font-medium text-[#CC5500]">Executive Assessment</span>
              </>
            )}
          </div>

          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6 leading-tight">
            Welcome to Your
            <span className="block bg-gradient-to-r from-[#CC5500] to-[#C5A065] bg-clip-text text-transparent">
              {isStudent ? 'Career Discovery Assessment' : 'Leadership Intelligence Assessment'}
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {isStudent 
              ? 'This assessment will help you discover your natural strengths, interests, and ideal career paths.'
              : 'This proprietary assessment decodes how you think, decide, execute, and lead.'
            }
          </p>
        </div>

        {/* What to Expect */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-heading font-semibold text-white mb-6 flex items-center gap-3">
            <Target className={`h-7 w-7 ${isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'}`} />
            What to Expect
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {isStudent ? (
              <>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#C5A065] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Career Discovery</h3>
                    <p className="text-sm text-gray-400">
                      Identify your natural interests and working style.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#C5A065] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Life Style Grid</h3>
                    <p className="text-sm text-gray-400">
                      Visual map of your preferred activities and needs.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#C5A065] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Career Path Recommendations</h3>
                    <p className="text-sm text-gray-400">
                      Job families and roles aligned with your profile.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#C5A065] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Development Roadmap</h3>
                    <p className="text-sm text-gray-400">
                      Strengths to leverage and areas to develop.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#CC5500] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Executive Scenarios</h3>
                    <p className="text-sm text-gray-400">
                      Real-world dilemmas faced by CEOs and board members.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#CC5500] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">40-Page Intelligence Report</h3>
                    <p className="text-sm text-gray-400">
                      Comprehensive analysis across 22 leadership dimensions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#CC5500] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Role Suitability Analysis</h3>
                    <p className="text-sm text-gray-400">
                      Compatibility scores and best-fit contexts.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-[#CC5500] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Integration Roadmap</h3>
                    <p className="text-sm text-gray-400">
                      90/180/365-day actionable development plan.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Assessment Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className={`h-6 w-6 ${isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'}`} />
              <h3 className="text-lg font-semibold text-white">Duration</h3>
            </div>
            <p className="text-gray-400">
              <strong className="text-white">{isStudent ? '15-20' : '20-25'} minutes</strong> for the assessment.
              A timer will track your progress.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className={`h-6 w-6 ${isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'}`} />
              <h3 className="text-lg font-semibold text-white">Confidentiality</h3>
            </div>
            <p className="text-gray-400">
              Your responses are <strong className="text-white">encrypted and confidential</strong>. Used solely for your profile.
            </p>
          </div>
        </div>

        {/* Guidelines */}
        <div className={`bg-gradient-to-br ${isStudent ? 'from-[#C5A065]/10' : 'from-[#CC5500]/10'} to-transparent border ${isStudent ? 'border-[#C5A065]/20' : 'border-[#CC5500]/20'} rounded-2xl p-8 mb-8`}>
          <h2 className="text-xl font-heading font-semibold text-white mb-4">Assessment Guidelines</h2>
          <ul className="space-y-3 text-gray-400">
            <li className="flex items-start gap-3">
              <span className={isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'} style={{fontWeight: 'bold'}}>1.</span>
              <span><strong className="text-white">Be authentic.</strong> Answer how you would genuinely respond, not what seems "correct."</span>
            </li>
            <li className="flex items-start gap-3">
              <span className={isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'} style={{fontWeight: 'bold'}}>2.</span>
              <span><strong className="text-white">Trust your instincts.</strong> Your first response is usually most accurate.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className={isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'} style={{fontWeight: 'bold'}}>3.</span>
              <span><strong className="text-white">Complete in one sitting.</strong> The assessment is designed for continuous flow.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className={isStudent ? 'text-[#C5A065]' : 'text-[#CC5500]'} style={{fontWeight: 'bold'}}>4.</span>
              <span><strong className="text-white">No distractions.</strong> Find a quiet space to focus completely.</span>
            </li>
          </ul>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            onClick={() => navigate(`/assessment?type=${assessmentType}`)}
            size="lg"
            className={`text-lg px-12 py-7 transition-all duration-300 ${
              isStudent
                ? 'bg-gradient-to-r from-[#C5A065] to-[#CC5500] hover:from-[#C5A065]/90 hover:to-[#CC5500]/90'
                : 'bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:from-[#CC5500]/90 hover:to-[#C5A065]/90'
            } text-white hover:scale-105 hover:shadow-xl hover:shadow-[#CC5500]/30`}
            data-testid="begin-assessment-button"
          >
            Begin {isStudent ? 'Career' : 'Leadership'} Assessment
            <ArrowRight className="ml-3 h-6 w-6" />
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            {isStudent ? '85 questions • 30 minutes' : '85 questions • 30 minutes'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-500">
            © 2026 LeadEconomics™ | Proprietary Leadership Intelligence Engine
          </p>
        </div>
      </footer>
    </div>
  );
};
